import React from 'react';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {REGISTRY_SUPPORTED_TYPES} from 'constants/RegistryConstants';
import {STORABLE_SCHEMA_TYPES, STORABLE_TYPES} from 'constants/SampleConstants';
import FileChip from 'files/chips/FileChip';
import StorableChip, {StorableChipProps} from 'inventory/chips/StorableChip';
import {SnapshotChip} from 'notebook/mediocre/core-ui/SnapshotChip';
import {PARTY_TYPES} from 'parties/PartyConstants';
import RequestV2SubmissionChip from 'processes/requests_v2/chips/RequestV2SubmissionChip';
import TestDefinitionChip from 'sample-tests/ui/test-definitions/chips/TestDefinitionChip';
import TestOrderChip from 'sample-tests/ui/test-orders/chips/TestOrderChip';
import BulkAssemblyChip, {BulkAssemblyChipProps} from 'ui-library/chips/BulkAssemblyChip';
import StageEntryChip, {StageEntryChipProps} from 'ui-library/chips/StageEntryChip';
import StorableSchemaChip from 'ui-library/chips/StorableSchemaChip';
import TagSchemaChip from 'ui-library/chips/TagSchemaChip';
import WorksheetChip from 'ui-library/chips/WorksheetChip';
import APIUtil from 'util/APIUtil';

import AnalysisChip from './AnalysisChip';
import AnalysisTemplateChip from './AnalysisTemplateChip';
import AutomationOutputFileChip from './AutomationOutputFileChip';
import BioentityChip from './BioentityChip';
import {ChipSize} from './Chip';
import {SharedSmartChipProps} from './ChipUtil';
import ConnectionChip from './ConnectionChip';
import DatasetChip from './DatasetChip';
import DocumentChip from './DocumentChip';
import EntryChip, {EntryChipProps} from './EntryChip';
import NoAccessChip, {NoAccessChipProps} from './NoAccessChip';
import PartyChip from './PartyChip';
import ProcedureChip from './ProcedureChip';
import ProcedureMethodExecutionInstanceChip from './ProcedureMethodExecutionInstanceChip';
import ProcedureRunChip from './ProcedureRunChip';
import ProcessChip from './ProcessChip';
import ProcessOutputChip from './ProcessOutputChip';
import ProcessTaskChip from './ProcessTaskChip';
import RequestChip from './RequestChip';
import RunChip from './RunChip';
import SampleDefinitionChip from './SampleDefinitionChip';
import StudyChip from './StudyChip';
import TemplateChip from './TemplateChip';
import {SmartChipType} from './useSmartChipQuery';
import WorklistChip, {WorklistChipProps} from './WorklistChip';

export type BenchChipProps =
  | EntryChipProps
  | NoAccessChipProps
  | SharedSmartChipProps<SmartChipType.ANALYSIS>
  | SharedSmartChipProps<SmartChipType.ANALYSIS_TEMPLATE>
  | SharedSmartChipProps<SmartChipType.AUTOMATION_OUTPUT_FILE>
  | SharedSmartChipProps<SmartChipType.BIOENTITY>
  | SharedSmartChipProps<SmartChipType.BULK_ASSEMBLY>
  | SharedSmartChipProps<SmartChipType.CONNECTION>
  | SharedSmartChipProps<SmartChipType.DATASET>
  | SharedSmartChipProps<SmartChipType.DOCUMENT>
  | SharedSmartChipProps<SmartChipType.FILE>
  | SharedSmartChipProps<SmartChipType.PARTY>
  | SharedSmartChipProps<SmartChipType.PROCEDURE>
  | SharedSmartChipProps<SmartChipType.PROCEDURE_RUN>
  | SharedSmartChipProps<SmartChipType.PROCESS>
  | SharedSmartChipProps<SmartChipType.PROCESS_OUTPUT>
  | SharedSmartChipProps<SmartChipType.PROCESS_TASK>
  | SharedSmartChipProps<SmartChipType.REQUEST>
  | SharedSmartChipProps<SmartChipType.REQUEST_V2_SUBMISSION>
  | SharedSmartChipProps<SmartChipType.RUN>
  | SharedSmartChipProps<SmartChipType.STORABLE_SCHEMA>
  | SharedSmartChipProps<SmartChipType.STUDY>
  | SharedSmartChipProps<SmartChipType.TAG_SCHEMA>
  | SharedSmartChipProps<SmartChipType.TEMPLATE>
  | StageEntryChipProps
  | StorableChipProps
  | WorklistChipProps
  | SharedSmartChipProps<SmartChipType.WORKSHEET>;

export function isNoAccessChipProps(props: {id?: string | null; size: ChipSize}): props is NoAccessChipProps {
  return !props.hasOwnProperty('id');
}

export const BENCH_CHIP_SUPPORTED_MODEL_TYPES = [
  ModelTypeConstants.ANALYSIS,
  ModelTypeConstants.ANALYSIS_TEMPLATE,
  ModelTypeConstants.AUTOMATION_OUTPUT_FILE,
  ModelTypeConstants.ASSAY_RUN,
  ModelTypeConstants.BATCH,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.CONNECTION,
  ModelTypeConstants.DATASET,
  ModelTypeConstants.DOCUMENT,
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.FILE,
  ModelTypeConstants.PROCEDURE,
  ModelTypeConstants.PROCEDURE_METHOD_EXECUTION_INSTANCE,
  ModelTypeConstants.PROCEDURE_RUN,
  ModelTypeConstants.PROCESS,
  ModelTypeConstants.PROCESS_OUTPUT,
  ModelTypeConstants.PROCESS_TASK,
  ModelTypeConstants.REQUEST,
  ModelTypeConstants.REQUEST_V2_SUBMISSION,
  ModelTypeConstants.SAMPLE_DEFINITION,
  ModelTypeConstants.STAGE_ENTRY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.TAG_SCHEMA,
  ModelTypeConstants.TEMPLATE,
  ModelTypeConstants.TEST_DEFINITION,
  ModelTypeConstants.TEST_ORDER,
  ModelTypeConstants.WORKLIST,
  ModelTypeConstants.WORKSHEET,
  ...PARTY_TYPES,
  ...REGISTRY_SUPPORTED_TYPES,
  ...STORABLE_TYPES,
  ...STORABLE_SCHEMA_TYPES,
] as const;

export function isModelBenchChipSupported(modelId: string): boolean {
  const modelType = APIUtil.apiIdToMaybeType(modelId);
  return (
    modelType != null &&
    (BENCH_CHIP_SUPPORTED_MODEL_TYPES as Readonly<Array<string>>).includes(modelType as string)
  );
}

export default function BenchChip(props: BenchChipProps): JSX.Element | null {
  if (isNoAccessChipProps(props) || props.id == null) {
    return <NoAccessChip {...(props as NoAccessChipProps)} />;
  } else if (isModelBenchChipSupported(props.id)) {
    const commonProps = {
      id: props.id,
      size: props.size,
      displayNameOverride: props.displayNameOverride,
      className: props.className ?? '',
    };
    if (APIUtil.isAnalysis(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="AnalysisChip">
          <AnalysisChip {...(props as SharedSmartChipProps<SmartChipType.ANALYSIS>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isAnalysisTemplate(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="AnalysisTemplateChip">
          <AnalysisTemplateChip {...(props as SharedSmartChipProps<SmartChipType.ANALYSIS_TEMPLATE>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isBiologicalEntity(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="BioentityChip">
          <BioentityChip {...(props as SharedSmartChipProps<SmartChipType.BIOENTITY>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isBulkAssembly(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="BulkAssemblyChip">
          <BulkAssemblyChip {...(props as BulkAssemblyChipProps)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isStorable(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="StorableChip">
          <StorableChip {...(props as StorableChipProps)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isStorableSchema(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="StorableSchemaChip">
          <StorableSchemaChip {...(props as SharedSmartChipProps<SmartChipType.STORABLE_SCHEMA>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isEntry(props.id)) {
      return (
        <SnapshotChip
          {...commonProps}
          chipType="EntryChip"
          useSecondaryText={BenchlingConfig.ELN_ENABLE_IDS_IN_ENTRY_CHIPS}
        >
          <EntryChip {...(props as EntryChipProps)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isConnection(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="ConnectionChip">
          <ConnectionChip {...(props as SharedSmartChipProps<SmartChipType.CONNECTION>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isDataset(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="DatasetChip">
          <DatasetChip {...(props as SharedSmartChipProps<SmartChipType.DATASET>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isDocument(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="DocumentChip">
          <DocumentChip {...(props as SharedSmartChipProps<SmartChipType.DOCUMENT>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isFile(props.id)) {
      return (
        <SnapshotChip
          {...commonProps}
          useSecondaryText={BenchlingConfig.ELN_ENABLE_IDS_IN_ENTRY_CHIPS}
          chipType="FileChip"
        >
          <FileChip {...(props as SharedSmartChipProps<SmartChipType.FILE>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isStageEntry(props.id)) {
      return (
        <SnapshotChip
          {...commonProps}
          chipType="StageEntryChip"
          useSecondaryText={BenchlingConfig.ELN_ENABLE_IDS_IN_ENTRY_CHIPS}
        >
          <StageEntryChip {...(props as StageEntryChipProps)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isParty(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="PartyChip">
          <PartyChip {...(props as SharedSmartChipProps<SmartChipType.PARTY>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isWorklist(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="WorklistChip">
          <WorklistChip {...(props as WorklistChipProps)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isWorksheet(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="WorksheetChip">
          <WorksheetChip {...(props as SharedSmartChipProps<SmartChipType.WORKSHEET>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isRequest(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="RequestChip">
          <RequestChip {...(props as SharedSmartChipProps<SmartChipType.REQUEST>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isRequestV2Submission(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="RequestV2SubmissionChip">
          <RequestV2SubmissionChip
            {...(props as SharedSmartChipProps<SmartChipType.REQUEST_V2_SUBMISSION>)}
          />
        </SnapshotChip>
      );
    } else if (APIUtil.isProcedure(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="ProcedureChip">
          <ProcedureChip {...(props as SharedSmartChipProps<SmartChipType.PROCEDURE>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isProcedureMethodExecutionInstance(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="ProcedureMethodExecutionInstanceChip">
          <ProcedureMethodExecutionInstanceChip
            {...(props as SharedSmartChipProps<SmartChipType.PROCEDURE_METHOD_EXECUTION_INSTANCE>)}
          />
        </SnapshotChip>
      );
    } else if (APIUtil.isProcedureRun(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="ProcedureRunChip">
          <ProcedureRunChip {...(props as SharedSmartChipProps<SmartChipType.PROCEDURE_RUN>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isProcess(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="ProcessChip">
          <ProcessChip {...(props as SharedSmartChipProps<SmartChipType.PROCESS>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isProcessOutput(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="ProcessOutputChip">
          <ProcessOutputChip {...(props as SharedSmartChipProps<SmartChipType.PROCESS_OUTPUT>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isProcessTask(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="ProcessTaskChip">
          <ProcessTaskChip {...(props as SharedSmartChipProps<SmartChipType.PROCESS_TASK>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isRun(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="RunChip">
          <RunChip {...(props as SharedSmartChipProps<SmartChipType.RUN>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isAutomationOutputFile(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="AutomationOutputFileChip">
          <AutomationOutputFileChip
            {...(props as SharedSmartChipProps<SmartChipType.AUTOMATION_OUTPUT_FILE>)}
          />
        </SnapshotChip>
      );
    } else if (APIUtil.isSampleDefinition(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="SampleDefinitionChip">
          <SampleDefinitionChip {...(props as SharedSmartChipProps<SmartChipType.SAMPLE_DEFINITION>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isStudy(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="StudyChip">
          <StudyChip {...(props as SharedSmartChipProps<SmartChipType.STUDY>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isTagSchema(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="TagSchemaChip">
          <TagSchemaChip {...(props as SharedSmartChipProps<SmartChipType.TAG_SCHEMA>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isEntryTemplate(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="TemplateChip">
          <TemplateChip {...(props as SharedSmartChipProps<SmartChipType.TEMPLATE> & {id: string})} />
        </SnapshotChip>
      );
    } else if (APIUtil.isTestDefinition(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="TestDefinitionChip">
          <TestDefinitionChip {...(props as SharedSmartChipProps<SmartChipType.TEST_DEFINITION>)} />
        </SnapshotChip>
      );
    } else if (APIUtil.isTestOrder(props.id)) {
      return (
        <SnapshotChip {...commonProps} chipType="TestOrderChip">
          <TestOrderChip {...(props as SharedSmartChipProps<SmartChipType.TEST_ORDER>)} />
        </SnapshotChip>
      );
    }
  }

  assertAndContinue(false, `Unexpected chip type with id='${props.id}'`);
  return <>{props.id}</>;
}
